import { IconButton } from '@/components/admin/reglages/reference-list';
import type { LinkFormRow } from '@/lib/link-form';
import { linkEntryProblemSentence } from '@/lib/link-labels';
import { pinIsBehind, pinOfChoice, pinWord } from '@/lib/references';
import { useReferenceVersions } from '@/lib/use-reference-versions';
import type {
  ExperienceCard,
  LinkEntryProblem,
  SessionSide,
  VersionPin,
} from '@matter_extractor/shared';
import { Icon, Select } from '@pms-digital/design-system';

/**
 * One row of the entries block (§11.4, planche `ReglagesPartage`; vitrine.md
 * §3.4, V16, V18, planche `atelier.html` section 5): an experience, the VERSION
 * it plays - `latest`, or a precise one - the SIDE it plays on, a trash, and the
 * refusal UNDER the row.
 *
 * ## The version is a select, and `latest` is its first option and its default
 *
 * « latest · aujourd'hui v15 (défaut) », then the versions of the experience
 * from the most recent to the oldest. Epingler a version is an explicit choice,
 * and a pinned entry that is behind says so under the row - the same signal the
 * editors give a pinned reference (« une v15 existe »), where the card of a link
 * carries its one-click update.
 *
 * ## The side is a pair of buttons beside it (V18)
 *
 * « dev », the default, and « prod »: on the `prod` side every `latest` of the
 * cascade is « latest prod », so the atelier replays exactly what the vitrine
 * plays. The line under the row says what the pair means for THIS entry, which
 * is what the operator cannot guess from two words.
 *
 * The refusal sits in the row rather than in a summary at the top of the form
 * because that is what the planche draws and because it is what makes it
 * actionable: an operator reading « une entrée ne résout pas » has to find which.
 * It is the error message of the experience's package `Select` - red border,
 * `aria-invalid` and the sentence under it, which names the next gesture
 * (`linkEntryProblemSentence`).
 *
 * The RETIRED experiences stay in the select, greyed by their label rather than
 * removed: a link being modified may already name one - §5.1 has the entry
 * disappear from the choice page, not from the link - and a select that dropped
 * it would show an empty row where the operator put something.
 */

export interface LinkEntryRowProps {
  readonly row: LinkFormRow;
  readonly index: number;
  readonly experiences: readonly ExperienceCard[];
  /** Null while the library is in flight - see the note in `link-form.tsx`. */
  readonly problem: LinkEntryProblem | null;
  readonly removable: boolean;
  readonly disabled: boolean;
  readonly onChange: (patch: Partial<LinkFormRow>) => void;
  readonly onRemove: () => void;
}

const SIDES: readonly { value: SessionSide; label: string; on: string }[] = [
  { value: 'dev', label: 'dev', on: 'bg-bleu-ciel-tint text-bleu-ciel-deep' },
  { value: 'prod', label: 'prod', on: 'bg-vert-tint text-vert-deep' },
];

export function LinkEntryRow({
  row,
  index,
  experiences,
  problem,
  removable,
  disabled,
  onChange,
  onRemove,
}: LinkEntryRowProps) {
  const refused = problem !== null;
  const card = experiences.find((one) => one.id === row.experienceId) ?? null;

  return (
    <li data-testid="partage-entree" className="space-y-2">
      <div className="flex flex-wrap items-end gap-3">
        {/* The test id on a wrapper: the package `Select` takes none, its
            trigger is the `combobox` inside. No experience yet is its
            placeholder (an empty value), never an option. */}
        <div data-testid="partage-entree-experience" className="min-w-0 flex-1 basis-40">
          <Select
            label="Expérience"
            compact
            value={row.experienceId}
            placeholder="Choisir une expérience"
            disabled={disabled}
            status={refused ? 'error' : 'default'}
            message={
              refused ? (
                <span data-testid="partage-entree-refus">{linkEntryProblemSentence(problem)}</span>
              ) : undefined
            }
            onValueChange={(experienceId) =>
              // A new experience starts again at `latest`: a pin names a version
              // of the experience it was chosen on, and means nothing on another.
              onChange({ experienceId, pin: 'latest' })
            }
            options={experiences.map((experience) => ({
              value: experience.id,
              label:
                experience.retiredAt === null ? experience.name : `${experience.name} (retirée)`,
            }))}
          />
        </div>

        <VersionSelect
          row={row}
          card={card}
          disabled={disabled}
          onPin={(pin) => onChange({ pin })}
        />

        <fieldset
          aria-label={`Côté de l’entrée ${card?.name ?? index + 1}`}
          className="m-0 flex min-w-0 shrink-0 flex-col gap-1 border-0 p-0"
        >
          <span className="font-semibold text-gray-800 text-caption">Côté</span>
          <span className="inline-flex h-10 overflow-hidden rounded-md border-2 border-gray-300 bg-white">
            {SIDES.map((side) => (
              <button
                key={side.value}
                type="button"
                data-testid={`partage-entree-cote-${side.value}`}
                aria-pressed={row.side === side.value}
                disabled={disabled}
                onClick={() => onChange({ side: side.value })}
                className={`cursor-pointer px-3 text-small focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-default ${
                  row.side === side.value ? `font-bold ${side.on}` : 'text-gray-700'
                } ${side.value === 'prod' ? 'border-gray-300 border-l-2' : ''}`}
              >
                {side.label}
              </button>
            ))}
          </span>
        </fieldset>

        {/* Kept mounted and DISABLED on the last row rather than removed: a
            control that disappears under the cursor is a control an operator
            hunts for, and §5.1 forbids an entry-less link. */}
        <IconButton
          label={`Retirer l’entrée ${index + 1}`}
          tone="danger"
          testId="partage-entree-retirer"
          disabled={disabled || !removable}
          onClick={onRemove}
        >
          <Icon name="delete" size={16} />
        </IconButton>
      </div>

      {row.experienceId === '' ? null : (
        <p data-testid="partage-entree-indice" className="text-gray-600 text-caption leading-4">
          {sideHint(row, card)}
        </p>
      )}
    </li>
  );
}

/**
 * « Version » - `latest` first, then every version of the experience, newest
 * first. The versions are read once an experience is named; until they arrive
 * the select offers `latest` and the pin it already holds, which is all a row
 * can honestly say about a library it has not read.
 */
function VersionSelect({
  row,
  card,
  disabled,
  onPin,
}: {
  row: LinkFormRow;
  card: ExperienceCard | null;
  disabled: boolean;
  onPin: (pin: VersionPin) => void;
}) {
  const read = useReferenceVersions('experience', row.experienceId, row.experienceId !== '');
  const developmentNumber = read?.development.number ?? card?.development.number ?? null;
  const productionNumber = read?.production?.number ?? card?.production?.number ?? null;
  const numbers = (read?.versions ?? []).map((version) => version.number).sort((a, b) => b - a);
  // A pin the read does not list (not read yet, or a stale one) stays selectable:
  // a select that dropped its own value would show another option as chosen.
  if (row.pin !== 'latest' && !numbers.includes(row.pin)) numbers.unshift(row.pin);

  const latestNumber = row.side === 'prod' ? productionNumber : developmentNumber;
  const latestLabel =
    latestNumber === null ? 'latest (défaut)' : `latest · aujourd’hui v${latestNumber} (défaut)`;

  return (
    <div data-testid="partage-entree-version" className="w-56">
      <Select
        label="Version"
        compact
        value={String(row.pin)}
        disabled={disabled}
        onValueChange={(choice) => {
          const pin = pinOfChoice(choice);
          if (pin !== null) onPin(pin);
        }}
        options={[
          { value: 'latest', label: latestLabel },
          ...numbers.map((number) => ({
            value: String(number),
            label: `v${number}${number === productionNumber ? ' · en prod' : ''}`,
          })),
        ]}
      />
    </div>
  );
}

/**
 * What the pin and the side mean for this entry - the two hints of the planche,
 * one line: `latest` follows the latest dev version, or on the `prod` side the
 * one the vitrine plays; a number is that very version, whose own `latest`
 * references follow the side.
 */
function sideHint(row: LinkFormRow, card: ExperienceCard | null): string {
  const side =
    row.side === 'prod'
      ? 'ce que joue la prod (latest prod)'
      : 'le travail en cours du dev (latest dev)';
  const developmentNumber = card?.development.number ?? null;
  if (row.pin === 'latest') {
    return row.side === 'prod'
      ? 'Côté prod : suit la dernière version en prod, et joue exactement ce que joue la prod (latest prod partout).'
      : 'Côté dev : suit la dernière version du dev, et joue le travail en cours du dev.';
  }
  const behind =
    developmentNumber !== null && pinIsBehind(row.pin, developmentNumber)
      ? ` Épinglée sur la ${pinWord(row.pin)} : une v${developmentNumber} existe.`
      : '';
  return `Côté ${row.side} : la ${pinWord(row.pin)} ; ses références en latest jouent ${side}.${behind}`;
}
